import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { MoreVert, Payment, Visibility, Discount, Edit, Search, CalendarToday } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { financeAPI } from '../../services/api';
import StatusBadge from '../../components/Finance/StatusBadge';
import { formatCurrency } from '../../components/Finance/formatCurrency';
import SubscriptionWizard from '../../components/Finance/SubscriptionWizard';

const PLAN_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', six_months: '6 Months', yearly: 'Yearly' };
function getNextDueState(nextDueDate) {
  if (!nextDueDate) return null;
  const due = new Date(nextDueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due - today) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return 'overdue';
  if (diffDays <= 7) return 'due_soon';
  return null;
}

const FinanceSubscriptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const outstandingRef = useRef(null);
  const dueTodayRef = useRef(null);

  const searchParams = new URLSearchParams(location.search || '');
  const scopeFromUrl = searchParams.get('scope');
  const viewFromUrl = searchParams.get('view');
  const outstandingScope =
    scopeFromUrl === 'overdue' || scopeFromUrl === 'imminent' || scopeFromUrl === 'all'
      ? scopeFromUrl
      : 'all';
  const showDueToday = viewFromUrl === 'due-today';

  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'active',
    country: '',
    currency: '',
    includeDeleted: false,
    search: '',
    page: 1,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput.trim(), page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const status = params.get('status');
    if (status != null) {
      setFilters((prev) => (prev.status === status ? prev : { ...prev, status, page: 1 }));
    }
  }, [location.search]);

  useEffect(() => {
    if (showDueToday && dueTodayRef.current) {
      dueTodayRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if ((outstandingScope === 'overdue' || outstandingScope === 'imminent') && outstandingRef.current) {
      outstandingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showDueToday, outstandingScope]);

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuSub, setRowMenuSub] = useState(null);

  const [overduePage, setOverduePage] = useState(1);
  const [overdueLimit, setOverdueLimit] = useState(20);

  const [wizardOpen, setWizardOpen] = useState(false);

  const { data, isLoading } = useQuery(
    ['financeSubscriptions', filters],
    async () => {
      const params = {
        status: filters.status || undefined,
        country: filters.country || undefined,
        currency: filters.currency || undefined,
        includeDeleted: filters.includeDeleted || undefined,
        search: (filters.search || '').trim() || undefined,
        page: filters.page,
        limit: filters.limit,
      };
      const res = await financeAPI.listSubscriptions(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

  const overdueQuery = useQuery(
    ['financeOverduePayments', { page: overduePage, limit: overdueLimit, scope: outstandingScope }],
    async () => {
      const params = { page: overduePage, limit: overdueLimit, scope: outstandingScope };
      const res = await financeAPI.getOverduePayments(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

  const dueTodayQuery = useQuery(
    ['financeDueTodayPayments', { enabled: showDueToday }],
    async () => {
      const res = await financeAPI.getDueTodayPayments({ page: 1, limit: 50 });
      return res.data;
    },
    { enabled: showDueToday, keepPreviousData: true }
  );

  const reminderMutation = useMutation(
    async ({ paymentId, channel, reminderType }) => {
      const res = await financeAPI.triggerPaymentReminder(paymentId, {
        channel,
        reminderType,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success('Reminder queued');
        queryClient.invalidateQueries('financeOverduePayments');
      },
      onError: (err) => {
        const msg =
          err.response?.data?.message ||
          (Array.isArray(err.response?.data?.errors)
            ? err.response.data.errors.join(', ')
            : 'Failed to trigger reminder');
        toast.error(msg);
      },
    }
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: field === 'page' ? value : 1,
    }));
  };

  const handleOpenDetails = (sub, options = {}) => {
    setRowMenuAnchor(null);
    setRowMenuSub(null);
    navigate(`/finance/subscriptions/${sub.id}`, { state: options });
  };

  const handleRowMenuOpen = (e, sub) => {
    e.stopPropagation();
    setRowMenuAnchor(e.currentTarget);
    setRowMenuSub(sub);
  };

  const handleRowMenuClose = () => {
    setRowMenuAnchor(null);
    setRowMenuSub(null);
  };

  const handleOpenPaymentDialog = (sub) => {
    handleOpenDetails(sub, { openPayment: true });
  };

  const handleOpenCreateSub = () => setWizardOpen(true);

  const rows = data?.data || [];
  const pagination = data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 };
  const totalCount = pagination.total || 0;
  const startItem = totalCount === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, totalCount);
  const overdueRows = overdueQuery.data?.data || [];
  const overduePagination =
    overdueQuery.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Page header – Student Billing Admin style */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" color="text.primary" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
            Student Billing Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor class balances and upcoming payment schedules.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button variant="outlined" sx={{ borderRadius: 2 }}>
            Export
          </Button>
          <Button variant="contained" onClick={handleOpenCreateSub} sx={{ borderRadius: 2 }}>
            + New subscription
          </Button>
        </Box>
      </Box>

      {/* Search + filters bar */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by student name, ID or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, bgcolor: 'grey.50' },
              }}
            />
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              Filter:
            </Typography>
            <TextField
              select
              size="small"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              sx={{ minWidth: 120 }}
              InputProps={{ sx: { borderRadius: 2 } }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              value={filters.currency}
              onChange={(e) => handleFilterChange('currency', e.target.value.toUpperCase())}
              displayEmpty
              sx={{ minWidth: 100 }}
              InputProps={{ sx: { borderRadius: 2 } }}
            >
              <MenuItem value="">Currency</MenuItem>
              {['USD', 'INR', 'GBP', 'CAD', 'AUD', 'SGD', 'NZD', 'JPY', 'AED'].map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  checked={!!filters.includeDeleted}
                  onChange={(e) => handleFilterChange('includeDeleted', e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Include deleted</Typography>}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Main table – Student Billing Admin style */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
        <TableContainer>
          <Table size="medium" sx={{ '& .MuiTableRow-root td': { py: 1.5 }, '& .MuiTableRow-root th': { py: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Student ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Subscription Plan</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Payment Due</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                  <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span>Due Amount</span>
                    <Typography component="span" variant="caption" sx={{ fontWeight: 400, textTransform: 'none', color: 'text.secondary' }}>
                      all open cycles
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>Loading...</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No subscriptions found.</TableCell>
                </TableRow>
              ) : (
                rows.map((sub) => {
                  const studentName = sub.student_first_name || sub.student_last_name
                    ? `${sub.student_first_name || ''} ${sub.student_last_name || ''}`.trim()
                    : (sub.student_display_id || sub.student_id || '').toString();
                  const planLabel = PLAN_LABELS[sub.subscription_model] || sub.subscription_model;
                  const planChipLabel = `${sub.classes_per_week || 0}x/week • ${planLabel}`;
                  const nextPaymentDue = sub.next_payment_due_date || sub.next_due_date || null;
                  const nextDueState = getNextDueState(nextPaymentDue);
                  return (
                    <TableRow
                      key={sub.id}
                      hover
                      onClick={() => handleOpenDetails(sub)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.50' },
                      }}
                    >
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {sub.student_display_id || '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">{studentName}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {sub.student_email || sub.country || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 10,
                              bgcolor: 'grey.200',
                              color: 'text.primary',
                              fontSize: '0.8rem',
                              fontWeight: 500,
                            }}
                          >
                            {planChipLabel}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {nextDueState === 'overdue' ? (
                              <StatusBadge status="overdue" />
                            ) : (
                              <>
                                <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        nextDueState === 'overdue'
                                          ? 'error.main'
                                          : nextDueState === 'due_soon'
                                            ? 'warning.main'
                                            : 'text.primary',
                                      fontWeight: nextDueState ? 600 : 400,
                                    }}
                                  >
                                    {nextPaymentDue || '—'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {(sub.open_invoices_count || 0) > 0
                                      ? `${sub.open_invoices_count} pending • ${(sub.open_invoice_months || []).slice(0, 2).join(', ')}${
                                          (sub.open_invoice_months || []).length > 2 ? '…' : ''
                                        }`
                                      : 'No pending invoices'}
                                  </Typography>
                                </Box>
                                {nextDueState === 'due_soon' && <StatusBadge status="due_soon" />}
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {Number(sub.open_due_amount || 0) > 0
                              ? formatCurrency(Number(sub.open_due_amount), sub.currency)
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Button size="small" variant="contained" startIcon={<Payment />} sx={{ ml: 0.5 }} onClick={() => handleOpenPaymentDialog(sub)}>
                            Record Payment
                          </Button>
                          <Button size="small" variant="outlined" sx={{ ml: 0.5 }} startIcon={<Visibility />} onClick={() => handleOpenDetails(sub)}>
                            View
                          </Button>
                          <IconButton size="small" onClick={(e) => handleRowMenuOpen(e, sub)} aria-label="More">
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {totalCount === 0 ? 0 : startItem} to {endItem} of {totalCount} students
          </Typography>
          <TablePagination
            component="div"
            count={totalCount}
            page={(filters.page || 1) - 1}
            onPageChange={(_, newPage) => handleFilterChange('page', newPage + 1)}
            rowsPerPage={filters.limit}
            onRowsPerPageChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10) || 20)}
            rowsPerPageOptions={[10, 20, 50, 100]}
            sx={{ border: 0 }}
          />
        </Box>
      </Paper>

      {/* Row action menu */}
      <Menu anchorEl={rowMenuAnchor} open={!!rowMenuAnchor} onClose={handleRowMenuClose}>
        <MenuItem
          onClick={() => {
            if (rowMenuSub) handleOpenDetails(rowMenuSub, { openDiscounts: true });
            handleRowMenuClose();
          }}
        >
          <ListItemIcon><Discount fontSize="small" /></ListItemIcon>
          <ListItemText>Discounts</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenuSub) handleOpenDetails(rowMenuSub, { openEdit: true });
            handleRowMenuClose();
          }}
        >
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit subscription</ListItemText>
        </MenuItem>
      </Menu>

      {showDueToday && (
        <Box mt={3} ref={dueTodayRef}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Payments due today
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Pending / overdue payment rows with due date = today.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Student</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dueTodayQuery.isLoading ? (
                    <TableRow><TableCell colSpan={5}>Loading...</TableCell></TableRow>
                  ) : (dueTodayQuery.data?.data || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5}>No payments due today.</TableCell></TableRow>
                  ) : (
                    (dueTodayQuery.data?.data || []).map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          {`${p.student_first_name || ''} ${p.student_last_name || ''}`.trim() || p.student_display_id || '—'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(p.amount, p.currency)}</TableCell>
                        <TableCell>{p.due_date}</TableCell>
                        <TableCell><StatusBadge status={p.status} /></TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/finance/subscriptions/${p.subscription_id}`, { state: { openPayment: true } })}
                          >
                            Record Payment
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      <Box mt={3} ref={outstandingRef}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
            {outstandingScope === 'overdue'
              ? 'Overdue invoices'
              : outstandingScope === 'imminent'
                ? 'Overdue & due soon'
                : 'Outstanding invoices'}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {outstandingScope === 'overdue'
              ? 'Past-due open invoice balances only.'
              : outstandingScope === 'imminent'
                ? 'Overdue and due within 7 days.'
                : 'Every open billing cycle with a balance (overdue, due soon, and upcoming).'}
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableRow-root td': { py: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Student</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overdueQuery.isLoading ? (
                  <TableRow><TableCell colSpan={6}>Loading...</TableCell></TableRow>
                ) : overdueRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6}>No outstanding invoice balances.</TableCell></TableRow>
                ) : (
                  overdueRows.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {p.student_display_id || (p.student_first_name || p.student_last_name ? `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim() : '—')}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 500,
                          color: p.status === 'overdue' ? 'error.main' : p.status === 'due_soon' ? 'warning.main' : 'text.primary',
                        }}
                      >
                        {formatCurrency(p.amount, p.currency)}
                      </TableCell>
                      <TableCell>{p.due_date}</TableCell>
                      <TableCell><StatusBadge status={p.status} /></TableCell>
                      <TableCell>{p.payment_method || '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ mr: 0.5 }}
                          onClick={() => navigate(`/finance/subscriptions/${p.subscription_id}`, { state: { openPayment: true } })}
                        >
                          Record Payment
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5 }}
                          onClick={() => {
                            if (!p.payment_id) return;
                            reminderMutation.mutate({ paymentId: p.payment_id, channel: 'whatsapp', reminderType: String(p.status) === 'overdue' ? 'overdue' : 'due_soon' });
                          }}
                          disabled={reminderMutation.isLoading || !p.payment_id}
                        >
                          Remind
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={overduePagination.total || 0}
            page={(overduePage || 1) - 1}
            onPageChange={(_, newPage) => setOverduePage(newPage + 1)}
            rowsPerPage={overdueLimit}
            onRowsPerPageChange={(e) => {
              setOverdueLimit(parseInt(e.target.value, 10) || 20);
              setOverduePage(1);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Paper>
      </Box>

      <SubscriptionWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(data) => {
          queryClient.invalidateQueries('financeSubscriptions');
          const id = data?.subscription?.id;
          if (id) navigate(`/finance/subscriptions/${id}`);
        }}
      />

    </Box>
  );
};

export default FinanceSubscriptions;

